import { LocationStrategy } from '@angular/common';
import {
  ApplicationConfig,
  DOCUMENT,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  RouteReuseStrategy,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { provideHighcharts } from 'highcharts-angular';
import { provideToastr, ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { FileHashLocationStrategy } from './core/file-hash-location-strategy';
import { AppRouteReuseStrategy } from './core/route-reuse-strategy';
import { ErrorOverlayToastrService } from './core/services/error-overlay-toastr.service';
import { GoogleMapsLoaderService } from './core/services/google-maps-loader.service';
import { WellService } from './core/services/well.service';
import { WellServiceHMI } from './core/services/well.service.hmi';
import { WellServiceKenLive } from './core/services/well.service.kenlive';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      // Load Google Maps script dynamically if not in HMI mode
      if (environment.isHMIMode) return;
      const document = inject(DOCUMENT);
      const loader = inject(GoogleMapsLoaderService);
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.mapKey}&loading=async&libraries=marker`;
      // Deliberately not awaited here — blocking the whole app's bootstrap on a third-party
      // script would delay every page for the sake of one, rarely-the-first-stop view.
      // MapViewComponent waits on GoogleMapsLoaderService.status() instead, only once (and if)
      // it actually mounts.
      script.onload = () => {
        // `loading=async` defers the API's own internal bootstrap past this event, so
        // google.maps.importLibrary (and google.maps.Map) can still be missing right when
        // onload fires — polling here avoids <google-map>'s ngOnInit calling
        // google.maps.importLibrary before it exists ("importLibrary is not a function").
        let attempts = 0;
        const untilReady = (): Promise<void> => {
          if (window.google?.maps?.importLibrary) {
            return window.google.maps.importLibrary('maps').then(() => undefined);
          }
          if (++attempts > 100) return Promise.reject(new Error('google.maps never initialized'));
          return new Promise((resolve) => setTimeout(resolve, 30)).then(untilReady);
        };
        untilReady().then(
          () => loader.markLoaded(),
          () => loader.markError(),
        );
      };
      script.onerror = () => loader.markError();
      document.head.appendChild(script);
    }),
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
    provideHighcharts({
      modules: () => [
        import('highcharts/esm/highcharts-3d'),
        import('highcharts/esm/modules/exporting'),
        import('highcharts/esm/modules/stock'),
        import('highcharts/esm/modules/draggable-points'),
      ],
    }),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-bottom-full-width',
      preventDuplicates: true,
      toastClass: 'ngx-toastr w-100 w-md-50 w-lg-25',
    }),
    {
      provide: WellService,
      useClass: environment.isHMIMode ? WellServiceHMI : WellServiceKenLive,
    },
    {
      provide: ToastrService,
      useClass: ErrorOverlayToastrService,
    },
    {
      provide: RouteReuseStrategy,
      useClass: AppRouteReuseStrategy,
    },
    ...(environment.useHashRouting
      ? [{ provide: LocationStrategy, useClass: FileHashLocationStrategy }]
      : []),
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { hideDelay: 150 },
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: { disablePagination: true },
    },
  ],
};
